/** Seconds of visible time after which the checkout form reports `checkout_form_<n>s` to Clarity. */
export const CHECKOUT_FORM_MILESTONES_S = [5, 15, 30, 60] as const;

/**
 * Longest stretch credited between two ticks. A phone that freezes the page without firing
 * `visibilitychange` (screen locked mid-tick, app switcher) would otherwise credit the whole gap
 * as time spent looking at the form.
 */
export const MAX_CREDITED_STEP_MS = 5_000;

export interface VisibleTimeTracker {
  /** Report the clock and whether the tab is visible now; fires every milestone just reached. */
  tick(nowMs: number, visibleNow: boolean): void;
  visibleMs(): number;
}

/**
 * Counts only the time the tab was visible: the stretch since the previous tick is credited when
 * the tab was visible at the previous tick. Each milestone fires once.
 */
export function createVisibleTimeTracker(args: {
  milestonesS: readonly number[];
  onMilestone: (seconds: number) => void;
}): VisibleTimeTracker {
  const pending = [...args.milestonesS].sort((a, b) => a - b);
  let visibleMs = 0;
  let last: { atMs: number; visible: boolean } | null = null;

  return {
    tick(nowMs, visibleNow) {
      if (last?.visible) {
        visibleMs += Math.min(Math.max(nowMs - last.atMs, 0), MAX_CREDITED_STEP_MS);
      }
      last = { atMs: nowMs, visible: visibleNow };
      while (pending.length > 0 && visibleMs >= pending[0]! * 1000) {
        args.onMilestone(pending.shift()!);
      }
    },
    visibleMs: () => visibleMs,
  };
}

export function checkoutFormMilestoneEventName(seconds: number): string {
  return `checkout_form_${seconds}s`;
}
